import {
  Component,
  computed,
  ContentChildren,
  Input,
  QueryList,
  WritableSignal,
  effect,
  SimpleChanges,
  inject,
} from '@angular/core';
import { Router } from '@angular/router';
import { SidebarMenuService } from '@core/services/sidebar-menu.service';

@Component({
  selector: 'app-sidebar-item',
  standalone: true,
  imports: [],
  templateUrl: './sidebar-item.component.html',
  styleUrl: './sidebar-item.component.scss',
})
export class SidebarItemComponent {
  private router = inject(Router);
  private sidebarMenuService = inject(SidebarMenuService);

  @Input() title!: string;
  @Input() icon: string = '';
  @Input() iconUrl: string = '';
  @Input() iconClass: string = '';
  @Input() isCollapsable: boolean = false;
  @Input() activeSignal!: WritableSignal<string | null>;
  @Input() route: string = '';
  @Input() newBtn: boolean = false;
  @Input() btnLink: string = '';

  @ContentChildren(SidebarItemComponent)
  nestedItems!: QueryList<SidebarItemComponent>;

  isExpanded: boolean = false;
  isSidebarOpen: boolean = false;

  readonly isSelfSelected = computed(() => {
    const active = this.activeSignal?.();
    return active !== null && active === this.title;
  });

  readonly isAnyChildSelected = computed(() => {
    const active = this.activeSignal?.();
    return this.nestedItems?.some((child) => child.title === active) ?? false;
  });

  private wasCollapsed = false;

  constructor() {
    effect(() => {
      this.isSidebarOpen = this.sidebarMenuService.isSidebarOpen();
      if (!this.isSidebarOpen && this.isCollapsable) {
        this.isExpanded = false;
        this.wasCollapsed = true;
      } else if (
        this.isSidebarOpen &&
        this.wasCollapsed &&
        this.isCollapsable &&
        this.hasSelectedChild()
      ) {
        this.isExpanded = true;
        this.wasCollapsed = false;
      }
    });
  }

  ngOnInit(): void {
    this.checkCurrentRoute();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isSidebarOpen']) {
      if (this.isAnyChildSelected() && this.isSidebarOpen && !this.isExpanded) {
        this.onClick(new MouseEvent('click'));
      } else if (
        !this.isAnyChildSelected() &&
        this.isSidebarOpen &&
        this.isExpanded
      ) {
        this.isExpanded = false;
      }
    }
  }

  ngAfterContentInit(): void {
    if (this.hasSelectedChild()) {
      this.isExpanded = true;
    }
  }

  onClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.isCollapsable) {
      event.preventDefault();
      this.isExpanded = !this.isExpanded;
      return;
    }
    this.activeSignal?.set(this.title);
    if (this.route) {
      this.router.navigate([this.route]);
    }
  }

  private hasSelectedChild(): boolean {
    return (
      this.nestedItems?.some(
        (child) => child.title === this.activeSignal?.(),
      ) ?? false
    );
  }

  checkCurrentRoute(): void {
    if (this.route && this.router.url.startsWith(this.route)) {
      this.activeSignal?.set(this.title);
    }
  }

  goToCreate(event: MouseEvent): void {
    event.stopPropagation();
    this.router.navigate([this.btnLink]);
  }
}
